/**
 * Fact-check and historical-aggregate leaf editors (Tailwind).
 * Options are filtered by the matrix + chosen event type; source_type_filter is
 * auto-derived (read-only unless multiple sources exist).
 */
import React from "react";
import { useBuilder } from "./BuilderContext";
import {
  factKeysForEvent, histKeysForEvent, aggregatesFor, sourceTypesFor,
  deriveSourceTypeFilter, isAdvancedOp, emptyFactCheck, emptyHistorical,
} from "./ruleModel";
import { CatalogAutocomplete, SchemePicker, ChipListInput } from "./inputs";
import { Field, Select, TextInput, Chip, cx } from "./ui";

const OP_LABEL = {
  eq: "Equals", neq: "Not equals", gt: "Greater than", gte: "Greater or equal",
  lt: "Less than", lte: "Less or equal", in_list: "In list", not_in_list: "Not in list",
  first_N_scans: "First N scans", all_of: "Complete all of", active_streak: "Active day streak",
};
const errAt = (errors, path) => errors.find((e) => e.path === path && e.severity === "error")?.message;

// ---------------------------------------------------------------------------
// Fact check
// ---------------------------------------------------------------------------

export function FactCheckForm({ condition, path, errors, onChange }) {
  const { meta, eventType } = useBuilder();
  const keys = factKeysForEvent(meta, eventType);
  const def = condition.key ? meta.fact_check_keys[condition.key] : null;
  const isList = condition.operator === "in_list" || condition.operator === "not_in_list";

  const setKey = (key) => {
    const d = meta.fact_check_keys[key];
    onChange({ ...emptyFactCheck(), key, operator: d?.operators?.[0] || "eq", value: "" });
  };
  const setOp = (operator) => {
    const nowList = operator === "in_list" || operator === "not_in_list";
    onChange({ ...condition, operator, value: nowList !== isList ? (nowList ? [] : "") : condition.value });
  };

  return (
    <div className="grid gap-3 md:grid-cols-3 items-start">
      <Field label="Field" error={errAt(errors, `${path}.key`)} hint={def?.meaning}>
        <Select value={condition.key || ""} onChange={setKey} placeholder="Select a field"
          error={errAt(errors, `${path}.key`)} options={keys.map((k) => ({ value: k.key, label: k.label }))} />
      </Field>

      {def && (
        <Field label="Condition" error={errAt(errors, `${path}.operator`)}>
          <Select value={condition.operator || ""} onChange={setOp} error={errAt(errors, `${path}.operator`)}
            options={def.operators.map((op) => ({ value: op, label: OP_LABEL[op] || op }))} />
        </Field>
      )}

      {def && (
        <Field label="Value" error={errAt(errors, `${path}.value`)} className="md:col-span-1">
          <ValueWidget fieldKey={condition.key} def={def} isList={isList} value={condition.value}
            onChange={(v) => onChange({ ...condition, value: v })} error={errAt(errors, `${path}.value`)} />
        </Field>
      )}
    </div>
  );
}

function ValueWidget({ fieldKey, def, isList, value, onChange, error }) {
  const { appUserTypes, states, schemeFilters } = useBuilder();

  if (def.value_widget === "scheme_picker") return <SchemePicker multiple={isList} value={value} onChange={onChange} error={error} />;
  if (def.value_widget === "catalog_autocomplete") return <CatalogAutocomplete catalog={def.catalog} multiple={isList} value={value} onChange={onChange} error={error} />;

  if (fieldKey === "user_type") {
    const opts = schemeFilters?.user_types?.length
      ? schemeFilters.user_types.map((u) => ({ value: u.name, label: u.name }))
      : appUserTypes;
    return <OptionSelect multiple={isList} options={opts} value={value} onChange={onChange} error={error} placeholder="Select user type" scoped={!!schemeFilters?.user_types?.length} />;
  }
  if (fieldKey === "user_state") {
    const opts = schemeFilters?.states?.length
      ? schemeFilters.states.map((s) => ({ value: s, label: s }))
      : states;
    return <OptionSelect multiple={isList} options={opts} value={value} onChange={onChange} error={error} placeholder="Select state" scoped={!!schemeFilters?.states?.length} />;
  }

  if (isList) return <ChipListInput value={value} onChange={onChange} error={error} />;
  return <TextInput type={def.type === "number" ? "number" : "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} error={error} />;
}

/** Dropdown/pill selector over a fixed option list. Single => Select, list => pills. */
function OptionSelect({ multiple, options, value, onChange, error, placeholder, scoped }) {
  if (!multiple) {
    return <Select value={value ?? ""} onChange={onChange} error={error} placeholder={placeholder} options={options} />;
  }
  const selected = (Array.isArray(value) ? value : []).map(String);
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div className={cx("rounded-lg border p-2", error ? "border-red-300" : "border-gray-300")}>
      <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
        {options.map((o) => (
          <button key={o.value} type="button" onClick={() => toggle(String(o.value))}
            className={cx("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors shrink-0",
              selected.includes(String(o.value)) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50")}>
            {o.label}
          </button>
        ))}
        {options.length === 0 && <span className="text-xs text-gray-400">No options</span>}
      </div>
      {scoped && <p className="mt-1 text-[11px] text-blue-600">Limited to this scheme's eligibility.</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Historical aggregate
// ---------------------------------------------------------------------------

export function HistoricalForm({ condition, path, errors, onChange }) {
  const { meta, eventType } = useBuilder();
  const keys = histKeysForEvent(meta, eventType);
  const def = condition.tracking_key ? meta.historical_keys[condition.tracking_key] : null;
  const aggregates = def ? aggregatesFor(def) : [];
  const advanced = isAdvancedOp(condition.operator);
  const nativeOps = meta.operators.historical_native;
  const advOps = meta.operators.historical_advanced;
  const trackingValueMode = def ? String(def.tracking_value) : "";

  const setKey = (tracking_key) => {
    const d = meta.historical_keys[tracking_key];
    const agg = aggregatesFor(d)[0];
    onChange({
      ...emptyHistorical(), tracking_key, aggregate_type: agg, operator: "gte",
      source_type_filter: deriveSourceTypeFilter(meta, eventType, d, agg),
      tracking_value: String(d.tracking_value).startsWith("fixed:") ? d.tracking_value.split(":")[1] : "",
    });
  };
  const setAggregate = (aggregate_type) => onChange({ ...condition, aggregate_type, source_type_filter: deriveSourceTypeFilter(meta, eventType, def, aggregate_type) });
  const setOp = (operator) => {
    const toAll = operator === "all_of";
    onChange({
      ...condition, operator,
      value: toAll ? [] : (Array.isArray(condition.value) ? "" : condition.value),
      tracking_value: toAll ? "*" : (condition.tracking_value === "*" ? "" : condition.tracking_value),
    });
  };

  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Field label="Track" error={errAt(errors, `${path}.tracking_key`)} hint={def?.meaning}>
        <Select value={condition.tracking_key || ""} onChange={setKey} placeholder="What to measure"
          error={errAt(errors, `${path}.tracking_key`)} options={keys.map((k) => ({ value: k.key, label: k.label }))} />
      </Field>

      {def && (aggregates.length > 1 ? (
        <Field label="Aggregate" hint="Count events, or sum their value">
          <Select value={condition.aggregate_type || ""} onChange={setAggregate}
            options={aggregates.map((a) => ({ value: a, label: a === "sum" ? "Sum (total)" : "Count" }))} />
        </Field>
      ) : (
        <Field label="Aggregate" hint="Locked for this metric">
          <Chip color="gray">{condition.aggregate_type}</Chip>
        </Field>
      ))}

      {def && (
        <Field label="Operator" error={errAt(errors, `${path}.operator`)} hint={advanced ? "Advanced — counts since the rule's start date" : "Threshold on the aggregate"}>
          <Select value={condition.operator || ""} onChange={setOp} error={errAt(errors, `${path}.operator`)}
            options={[
              { group: "Threshold", options: nativeOps.map((op) => ({ value: op, label: OP_LABEL[op] || op })) },
              { group: "Advanced", options: advOps.map((op) => ({ value: op, label: OP_LABEL[op] || op })) },
            ]} />
        </Field>
      )}

      {def && condition.operator !== "all_of" && !trackingValueMode.startsWith("fixed:") && (
        <Field label="Which one" error={errAt(errors, `${path}.tracking_value`)}>
          {trackingValueMode === "scheme_or_any" ? (
            <SchemePicker value={condition.tracking_value} onChange={(v) => onChange({ ...condition, tracking_value: v })} error={errAt(errors, `${path}.tracking_value`)} />
          ) : (def.tracking_value === "specific" && (condition.tracking_key.includes("product") || condition.tracking_key.includes("category"))) ? (
            <CatalogAutocomplete catalog={condition.tracking_key.includes("category") ? "categories" : "products"}
              value={condition.tracking_value} onChange={(v) => onChange({ ...condition, tracking_value: v })} error={errAt(errors, `${path}.tracking_value`)} />
          ) : (
            <TextInput value={condition.tracking_value || ""} onChange={(e) => onChange({ ...condition, tracking_value: e.target.value })} error={errAt(errors, `${path}.tracking_value`)} />
          )}
        </Field>
      )}

      {condition.operator === "all_of" ? (
        <>
          <Field label="Items to complete" error={errAt(errors, `${path}.value`)} className="md:col-span-2">
            <ChipListInput value={condition.value} onChange={(v) => onChange({ ...condition, value: v })} error={errAt(errors, `${path}.value`)} />
          </Field>
          <Field label="Min scans per item">
            <TextInput type="number" value={condition.min_scans_per_item ?? 1} onChange={(e) => onChange({ ...condition, min_scans_per_item: Number(e.target.value) })} />
          </Field>
        </>
      ) : def && (
        <Field label={advanced ? "N" : "Value"} error={errAt(errors, `${path}.value`)}>
          <TextInput type="number" value={condition.value ?? ""} onChange={(e) => onChange({ ...condition, value: e.target.value })} error={errAt(errors, `${path}.value`)} />
        </Field>
      )}

      {def && !advanced && (
        <Field label="Time window (days)" error={errAt(errors, `${path}.time_window_days`)} hint="Rolling look-back window">
          <TextInput type="number" value={condition.time_window_days ?? ""} onChange={(e) => onChange({ ...condition, time_window_days: Number(e.target.value) })} error={errAt(errors, `${path}.time_window_days`)} />
        </Field>
      )}

      {def && condition.aggregate_type && (
        <div className="md:col-span-2">
          <SourceTypeFilter def={def} condition={condition} onChange={onChange} error={errAt(errors, `${path}.source_type_filter`)} />
        </div>
      )}
    </div>
  );
}

function SourceTypeFilter({ def, condition, onChange, error }) {
  const { meta } = useBuilder();
  const allowed = sourceTypesFor(def, condition.aggregate_type);
  const selected = condition.source_type_filter || [];
  const labelOf = (s) => meta.source_types.find((x) => x.name === s)?.label || s;

  if (allowed.length <= 1) {
    return (
      <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-800">
        Source: <b>{allowed.map(labelOf).join(", ") || "—"}</b> — auto-set for this metric.
      </div>
    );
  }
  const toggle = (s) => onChange({ ...condition, source_type_filter: selected.includes(s) ? selected.filter((x) => x !== s) : [...selected, s] });
  return (
    <div>
      <p className={cx("text-xs font-semibold mb-1", error ? "text-red-600" : "text-gray-600")}>Count from source(s){error ? ` — ${error}` : ""}</p>
      <div className="flex flex-wrap gap-1.5">
        {allowed.map((s) => (
          <button key={s} type="button" onClick={() => toggle(s)}
            className={cx("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
              selected.includes(s) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50")}>
            {labelOf(s)}
          </button>
        ))}
      </div>
    </div>
  );
}
